import { useTranslation } from 'react-i18next'
import Card from '@/components/ui/Card'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import type { ZakatLivestockInput, CalendarType } from '@/types/zakat'
import { Package } from 'lucide-react'

interface ZakatLivestockFormProps {
  input: ZakatLivestockInput
  calendar: CalendarType
  onInputChange: (input: ZakatLivestockInput) => void
  onCalendarChange: (calendar: CalendarType) => void
  onCalculate: () => void
}

export default function ZakatLivestockForm({
  input,
  calendar,
  onInputChange,
  onCalendarChange,
  onCalculate,
}: ZakatLivestockFormProps) {
  const { t } = useTranslation()
  const handleInputChange = (field: keyof ZakatLivestockInput, value: string) => {
    const numValue = parseFloat(value) || 0
    onInputChange({
      ...input,
      [field]: numValue,
    })
  }

  return (
    <Card>
      <div className="flex items-center gap-3 mb-6">
        <Package className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('zakat.livestock.title')}</h3>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          {t('zakat.yearOfHawl')}
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="calendar"
              value="hijri"
              checked={calendar === 'hijri'}
              onChange={() => onCalendarChange('hijri')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.hijri')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="calendar"
              value="gregorian"
              checked={calendar === 'gregorian'}
              onChange={() => onCalendarChange('gregorian')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.gregorian')}</span>
          </label>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <Input
          type="number"
          label={t('zakat.livestock.camels')}
          value={input.camels.toString()}
          onChange={(e) => handleInputChange('camels', e.target.value)}
          placeholder={t('zakat.livestock.placeholderCamels')}
        />
        <Input
          type="number"
          label={t('zakat.livestock.sheep')}
          value={input.sheep.toString()}
          onChange={(e) => handleInputChange('sheep', e.target.value)}
          placeholder={t('zakat.livestock.placeholderSheep')}
        />
        <Input
          type="number"
          label={t('zakat.livestock.cattle')}
          value={input.cattle.toString()}
          onChange={(e) => handleInputChange('cattle', e.target.value)}
          placeholder={t('zakat.livestock.placeholderCattle')}
        />
      </div>

      <Button variant="primary" size="lg" className="w-full" onClick={onCalculate}>
        {t('zakat.calculateYourZakat')}
      </Button>
    </Card>
  )
}

